import React from "react";
import { toast } from "sonner";
import { Loader2, Receipt, AlertCircle } from "lucide-react";
import { businessApi } from "../../lib/api";

function fmt(v) {
  if (v === null || v === undefined) return "—";
  return new Intl.NumberFormat("da-DK", { style: "currency", currency: "DKK" }).format(v);
}

export default function BusinessRateCardPage() {
  const [card, setCard] = React.useState(null);
  const [loading, setLoading] = React.useState(true);

  React.useEffect(() => {
    (async () => {
      try {
        const { data } = await businessApi().get("/b2b/2r5/rate-card");
        setCard(data);
      } catch {
        toast.error("Kunne ikke hente prisliste");
      } finally { setLoading(false); }
    })();
  }, []);

  if (loading) return <div className="flex items-center gap-2 text-brand-text2"><Loader2 className="animate-spin" size={16}/> Indlæser…</div>;
  if (!card?.active) {
    return (
      <div className="rounded-lg border border-amber-200 bg-amber-50 p-6" data-testid="rate-card-missing">
        <AlertCircle className="text-amber-700 mb-2" size={28}/>
        <h2 className="font-bold mb-1">Ingen aktiv prisliste</h2>
        <p className="text-sm text-amber-800">Der er ingen aktiv prisliste tilknyttet din virksomhed. Kontakt EMA Kurer på <a className="font-semibold underline" href="mailto:erhverv@emakurer.dk">erhverv@emakurer.dk</a> for at få oprettet din prisliste.</p>
      </div>
    );
  }
  const p = card.rate_card.prices || {};
  return (
    <div className="space-y-4 max-w-3xl" data-testid="business-rate-card-page">
      <header>
        <div className="flex items-center gap-2 text-xs text-brand-text2">
          <Receipt size={14}/> Prisliste · gyldig fra {card.rate_card.effective_from}
        </div>
        <h1 className="text-2xl font-bold text-brand-dark">{card.rate_card.name}</h1>
        <p className="text-sm text-brand-text2">Alle beløb i DKK. Moms tillægges {p.vat_pct ?? 25}%.</p>
      </header>

      <div className="rounded-lg border border-brand bg-white overflow-hidden">
        <table className="w-full text-sm">
          <tbody>
            {[
              ["Standard pr. pakke", p.default_per_parcel_dkk],
              ["Same Day", p.same_day_dkk],
              ["Next Day", p.next_day_dkk],
              ["Retur", p.return_dkk],
              ["Mislykket levering (gebyr)", p.failed_delivery_fee_dkk],
              ["Manuel label-fee", p.manual_label_fee_dkk],
              ["Ulæselig/forkert label", p.unreadable_label_fee_dkk],
              ["Manuel booking-korrektion", p.manual_booking_correction_fee_dkk],
              ["Ventetid pr. 15 min", p.waiting_time_fee_dkk_per_15min],
              ["Særhåndtering", p.special_handling_fee_dkk],
              ["Minimum-faktura", p.minimum_invoice_dkk],
            ].filter((row) => row[1] !== null && row[1] !== undefined).map(([label, value]) => (
              <tr key={label} className="border-t border-brand">
                <td className="px-4 py-2 text-brand-text2">{label}</td>
                <td className="px-4 py-2 text-right font-semibold">{fmt(value)}</td>
              </tr>
            ))}
            {p.volume_discount_pct ? (
              <tr className="border-t border-brand">
                <td className="px-4 py-2 text-brand-text2">Volumenrabat</td>
                <td className="px-4 py-2 text-right font-semibold">{p.volume_discount_pct}%</td>
              </tr>
            ) : null}
          </tbody>
        </table>
      </div>

      <p className="text-xs text-brand-text2">Har du spørgsmål til din prisliste? Kontakt EMA Kurer på <a className="font-semibold text-brand-red" href="mailto:erhverv@emakurer.dk">erhverv@emakurer.dk</a>.</p>
    </div>
  );
}
